// Pure logic for the Shot Map widget (FE6) — DOM-free, unit-tested. The widget
// (ShotMapWidget.tsx) is just an SVG view over what these functions return.
//
// The data comes from GET /player-shots. Two facts, confirmed by inspecting a
// real payload (Isak 8819 / comp 2 / season 52025, 17 shots) before writing this:
//
//  1. Coordinates are the StatsBomb pitch frame: x in [0,120] along the length
//     (the goal being attacked is at x=120), y in [0,80] across. Shots cluster in
//     the final third (Isak's ranged x 92–110, y 16–55) — so we draw only the
//     attacking third and place the goal at the TOP (attacking upward).
//  2. Outcome + xG live INSIDE the opaque event blobs, not as top-level fields:
//     `rawEventAttributes.shot.outcome.name` ("Goal" | "Saved" | "Off T" |
//     "Blocked" | "Wayward" | ...) and `rawEventAttributes.shot.statsbomb_xg`
//     (0..1). `enrichedEventAttributes.on_target` (bool) is a convenient flag.
//     Catalyst derives these via a "describe" layer we don't have, so we read the
//     raw blob directly. (This blind spot is exactly what hid the #49 team_id bug,
//     so the shape was verified live first.)

import type { PlayerShot } from "@catbento/shared";

/** The pitch frame we render: the attacking third, goal at x=120. Kept as an
 *  object so both the mapping math and the SVG markings share one source. */
export const PITCH = {
  length: 120, // full-pitch x extent (goal being attacked at x=120)
  width: 80, // full-pitch y extent
  /** Show x from here to the goal line. 75 frames the attacking ~40% snugly —
   *  shots cluster near goal (Isak's were x 92–110), so a deeper band just leaves
   *  the near half empty. A genuine long-range effort (x<75, ~45+ yards out) is
   *  rare and clamps to the near edge rather than falling off-canvas. */
  viewMinX: 75,
} as const;

/** SVG viewBox derived from PITCH: width spans the full pitch width (y 0..80),
 *  height spans the shown length (x viewMinX..120). We render in pitch units so
 *  the viewBox itself does the scaling — no per-dimension pixel math. */
export const VIEW = {
  width: PITCH.width, // 80
  height: PITCH.length - PITCH.viewMinX, // 60
} as const;

export type ShotOutcome = "goal" | "on_target" | "off_target";

/** Colours for each outcome. Local to the widget — the shared theme has no
 *  categorical outcome palette. Green = goal, amber = on target (saved), muted =
 *  off target / blocked / wayward. */
export const OUTCOME_COLOR: Record<ShotOutcome, string> = {
  goal: "#4ade80",
  on_target: "#fbbf24",
  off_target: "#64748b",
};

export const OUTCOME_LABEL: Record<ShotOutcome, string> = {
  goal: "Goal",
  on_target: "On target",
  off_target: "Off target",
};

// --- safe accessors over the opaque jsonb blobs ---------------------------

function shotObject(shot: PlayerShot): Record<string, unknown> | null {
  const raw = shot.rawEventAttributes;
  if (typeof raw !== "object" || raw === null) return null;
  const s = (raw as Record<string, unknown>).shot;
  return typeof s === "object" && s !== null ? (s as Record<string, unknown>) : null;
}

/** The StatsBomb outcome name, e.g. "Goal", "Saved", "Off T". null if absent. */
export function outcomeName(shot: PlayerShot): string | null {
  const s = shotObject(shot);
  const outcome = s?.outcome;
  if (typeof outcome !== "object" || outcome === null) return null;
  const name = (outcome as Record<string, unknown>).name;
  return typeof name === "string" ? name : null;
}

function onTargetFlag(shot: PlayerShot): boolean | null {
  const en = shot.enrichedEventAttributes;
  if (typeof en !== "object" || en === null) return null;
  const v = (en as Record<string, unknown>).on_target;
  return typeof v === "boolean" ? v : null;
}

/** StatsBomb model xG for the shot (0..1), or null if absent. */
export function shotXg(shot: PlayerShot): number | null {
  const s = shotObject(shot);
  const xg = s?.statsbomb_xg;
  return typeof xg === "number" ? xg : null;
}

/**
 * Three-way classification for colouring. Goal wins outright; otherwise a shot
 * is "on target" if the enriched flag says so OR the outcome name is a save
 * (the flag is the primary signal; the name is the fallback when it's missing);
 * everything else (Off T, Blocked, Wayward, Post, unknown) is "off target".
 */
export function classifyShot(shot: PlayerShot): ShotOutcome {
  const name = outcomeName(shot);
  if (name === "Goal") return "goal";
  if (onTargetFlag(shot) === true) return "on_target";
  if (name !== null && name.startsWith("Saved")) return "on_target";
  return "off_target";
}

// --- geometry -------------------------------------------------------------

/**
 * Map a StatsBomb pitch coordinate to the SVG viewBox (attacking-third view,
 * goal at the top). x is clamped to the shown band so a rare long-range shot
 * still lands at the bottom edge rather than off-canvas. Pure.
 *
 *   sx = y                    (0..80 across → viewBox width)
 *   sy = 120 - x             (x=120 goal-line → sy 0 top; x=viewMinX → bottom)
 */
export function pitchToSvg(x: number, y: number): { sx: number; sy: number } {
  const clampedX = Math.min(PITCH.length, Math.max(PITCH.viewMinX, x));
  return { sx: y, sy: PITCH.length - clampedX };
}

/**
 * Dot radius (in viewBox/pitch units) from xG: bigger = higher chance. sqrt so
 * small xG values stay visible and the scale isn't dominated by rare high-xG
 * shots. A shot with no xG gets a fixed middling radius. Pure.
 */
export function dotRadius(xg: number | null): number {
  if (xg === null) return 1.4;
  const clamped = Math.min(1, Math.max(0, xg));
  return 1.1 + Math.sqrt(clamped) * 2.0;
}

// --- summary (for the header line + legend counts) ------------------------

export interface ShotSummary {
  total: number;
  goals: number;
  onTarget: number;
  offTarget: number;
  totalXg: number;
}

/** Roll up a set of shots for the widget header ("17 shots · 3 goals · 2.9 xG")
 *  and the legend. Pure. */
export function summarizeShots(shots: PlayerShot[]): ShotSummary {
  const summary: ShotSummary = { total: shots.length, goals: 0, onTarget: 0, offTarget: 0, totalXg: 0 };
  for (const shot of shots) {
    const outcome = classifyShot(shot);
    if (outcome === "goal") summary.goals++;
    else if (outcome === "on_target") summary.onTarget++;
    else summary.offTarget++;
    const xg = shotXg(shot);
    if (xg !== null) summary.totalXg += xg;
  }
  return summary;
}
